import type {Ref} from 'react';
import {useTranslation} from 'react-i18next';
import {Flag, Users, UsersRound} from 'lucide-react';
import {Button, RadioCardList} from '@chekinapp/ui';
import {GROUP_TYPES} from '@chekin/core';
import {useOnboardingStore} from '@chekin/shared';
import {useGuestGroup} from 'hooks/useGuestGroup';
import {PropertyLinkShell} from 'pages/SearchReservation/components/PropertyLinkShell';
import {SignedInAs, StepHeading} from './StepParts';

const SELECTABLE_TYPES = [GROUP_TYPES.family, GROUP_TYPES.group, GROUP_TYPES.tourist];

type Props = {
  columnRef: Ref<HTMLDivElement>;
  onDone: () => void;
};

function TripStep({columnRef, onDone}: Props) {
  const {t} = useTranslation();
  const {guestGroup} = useGuestGroup();
  const groupType = useOnboardingStore(state => state.groupType);
  const setGroupType = useOnboardingStore(state => state.setGroupType);

  const storedType = groupType ?? guestGroup?.group_type;
  const selected = SELECTABLE_TYPES.includes(storedType as GROUP_TYPES)
    ? (storedType as GROUP_TYPES)
    : GROUP_TYPES.family;

  const options = [
    {
      value: GROUP_TYPES.family,
      label: t('family'),
      description: t('auth.trip_type_family_hint'),
      icon: <UsersRound />,
    },
    {
      value: GROUP_TYPES.group,
      label: t('group'),
      description: t('auth.trip_type_group_hint'),
      icon: <Users />,
    },
    {
      value: GROUP_TYPES.tourist,
      label: t('tourist_group'),
      description: t('auth.trip_type_tourist_hint'),
      icon: <Flag />,
    },
  ];

  const handleConfirm = () => {
    setGroupType(selected);
    onDone();
  };

  return (
    <PropertyLinkShell
      columnRef={columnRef}
      dock={
        <Button className="w-full" onClick={handleConfirm}>
          {t('continue')}
        </Button>
      }
    >
      <SignedInAs />
      <StepHeading lead={t('choose_the_option_that_best_describes_this_reservation')}>
        {t('how_are_you_traveling')}
      </StepHeading>
      <RadioCardList
        options={options}
        value={selected}
        onChange={value => setGroupType(value as GROUP_TYPES)}
      />
    </PropertyLinkShell>
  );
}

export {TripStep};
